import {
  AddNewOrder,
  AdvanceBatchSingle,
  Analytics,
  Claims,
  ClaimDetails,
  ClientError,
  Dashboard,
  EditOrder,
  Finance,
  FinanceSingle,
  Import,
  Login,
  NoData,
  NotFound,
  OrderDetails,
  Orders,
  Pickups,
  PickupSingle,
  RtoDetails,
  ServerError,
  TicketCenter,
  Unibox,
  Engage,
  SelectService,
  OrderSummary,
} from '@/pages';
import WorkspacesSection from '@/components/Workspace/WorkspacesSection';
import ResendPage from '@/pages/resend';
import SettingsProfile from '@/pages/settings/profile';
import SettingsLocations from '@/pages/settings/locations';
import SettingsLocationNew from '@/pages/settings/locations/new';
import SettingsLocationEdit from '@/pages/settings/locations/edit';
import SettingsTeamMembers from '@/pages/settings/team-members';
import SettingsTeamMembersCreate from '@/pages/settings/team-members/create';
import SettingsTeamMembersEdit from '@/pages/settings/team-members/edit';
import SettingsAccessTokens from '@/pages/settings/access-tokens';
import SettingsAccessTokensCreate from '@/pages/settings/access-tokens/create';
import SettingsWebhooks from '@/pages/settings/webhooks';
import SettingsWebhooksCreate from '@/pages/settings/webhooks/create';
import SettingsWebhooksEdit from '@/pages/settings/webhooks/edit';
import SettingsFiles from '@/pages/settings/files';
import SettingsBrandedTracking from '@/pages/settings/branded-tracking';
import { ServicePreferenceSection } from '@/components/SettingsCards/ServicePreferenceSection';
import { PricingPlanSection } from '@/components/SettingsCards/PricingPlanSection';
import SettingsAddressBook from '@/pages/settings/address-book';
import SettingsAddressBookNew from '@/pages/settings/address-book/new';
import SettingsAddressBookEdit from '@/pages/settings/address-book/edit';
import SettingsProductCatalog from '@/pages/settings/product-catalog';
import SettingsProductCatalogNew from '@/pages/settings/product-catalog/new';
import SettingsProductCatalogEdit from '@/pages/settings/product-catalog/edit';
import { ENGAGE_ENABLED, UNIBOX_ENABLED } from './setup.config';
import { IRoute } from '../types/route';
import { FinanceTab, PermissionName } from '@/types';
import { Main } from '@/layout';
import Register from '@/pages/register';
import Verify from '@/pages/verify';
import OrderRtoSingle from '@/pages/pickups/RTOsingle';
import { Navigate } from 'react-router-dom';
import MagicToken from '@/pages/magic-token';
import SingleTicket from '@/pages/ticket-center/single';
import DefaultRedirect from '@/routes/DefaultRedirect';
import SearchResultsPage from '@/pages/search';

const ROUTE = {
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_LOCATIONS: '/settings/locations',
  SETTINGS_LOCATIONS_NEW: '/settings/locations/new',
  SETTINGS_LOCATIONS_EDIT: '/settings/locations/:id/edit',
  SETTINGS_TEAM_MEMBERS: '/settings/team-members',
  SETTINGS_TEAM_MEMBERS_CREATE: '/settings/team-members/create',
  SETTINGS_TEAM_MEMBERS_EDIT: '/settings/team-members/:id/edit',
  SETTINGS_ADDRESS_BOOK: '/settings/address-book',
  SETTINGS_ADDRESS_BOOK_NEW: '/settings/address-book/new',
  SETTINGS_ADDRESS_BOOK_EDIT: '/settings/address-book/:id/edit',
  SETTINGS_PRODUCT_CATALOG: '/settings/product-catalog',
  SETTINGS_PRODUCT_CATALOG_NEW: '/settings/product-catalog/new',
  SETTINGS_PRODUCT_CATALOG_EDIT: '/settings/product-catalog/:uid/edit',
  SETTINGS_ACCESS_TOKENS: '/settings/access-tokens',
  SETTINGS_ACCESS_TOKENS_CREATE: '/settings/access-tokens/create',
  SETTINGS_WEBHOOKS: '/settings/webhooks',
  SETTINGS_WEBHOOKS_CREATE: '/settings/webhooks/create',
  SETTINGS_WEBHOOKS_EDIT: '/settings/webhooks/:id/edit',
  SETTINGS_FILES: '/settings/files',
  SETTINGS_BRANDED_TRACKING: '/settings/branded-tracking',
  SETTINGS_CARRIERS: '/settings/carriers',
  SETTINGS_SHIPPING_RULES: '/settings/shipping-rules',
  SETTINGS_SERVICE_PREFERENCE: '/settings/service-preference',
  SETTINGS_WORKSPACES: '/settings/workspaces',
  SETTINGS_PRICING_PLAN: '/settings/pricing-plan',
  HOME: '/',
  DASHBOARD: '/dashboard',
  ANALYTICS: '/analytics',
  INBOX: '/inbox',
  ENGAGE: '/engage',
  ORDERS: '/orders',
  PICKUPS: '/pickups',
  RTO_LIST: '/pickups#rto',
  PICKUPS_DETAILS: '/pickups/details',
  ORDER_DETAILS: '/orders',
  ORDER_DETAILS_RTO: '/orders/rto', // DEPRECATED: Use PICKUP_RTO_DETAILS instead
  PICKUP_RTO_DETAILS: '/pickups/rto/:id',
  FINANCE: '/finance',
  FINANCE_DETAILS: '/finance/details/:id',
  ADVANCE_COD: '/advance-cod',
  ADVANCE_COD_BATCH_DETAILS: '/advance-cod/batches/:uid',
  TICKET_CENTER: '/ticket-center',
  TICKET_CENTER_DETAILS: '/ticket-center/details/:id',
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY: '/verify',
  ADDNEWORDER: '/orders/add',
  SELECT_CARRIER: '/orders/add/select-carrier',
  SELECT_SERVICE: '/orders/add/select-service',
  ORDER_SUMMARY: '/orders/add/summary',
  ORDER_EDIT: '/orders/edit/:orderUid',
  IMPORT: '/import',
  RESEND: '/resend',
  SEARCH: '/search',
  CLAIMS: '/claims',
  CLAIMS_DETAILS: '/claims',
  SETTINGS: '/settings',
  RTO_DETAILS: '/rto/details',
  NO_DATA: '/no-data',
  ERROR_400: '/400',
  ERROR_500: '/500',
  NOT_VERIFIED: '/not-verified',
  MAGIC_TOKEN: '/login-with-magic-token/:magicToken',
  NOT_FOUND: '*',
};

const routes: IRoute[] = [
  // {
  //   path: ROUTE.HOME,
  //   element: (
  //     <Main>
  //       <Orders />
  //     </Main>
  //   ),
  //   private: true,
  // },
  {
    path: ROUTE.HOME,
    element: <DefaultRedirect />, // Smart redirect based on user permissions
    private: true,
  },
  {
    path: ROUTE.DASHBOARD,
    element: (
      <Main>
        <Dashboard />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.ANALYTICS,
    element: (
      <Main>
        <Analytics />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  // UniBox — gated with the sidebar entry, never separately: hiding only the
  // link would leave /inbox reachable by URL. With the gate off, /inbox falls
  // through to the `'*'` catch-all. See UNIBOX_ENABLED in setup.config.ts.
  ...(UNIBOX_ENABLED
    ? [
        {
          path: ROUTE.INBOX,
          element: (
            <Main>
              <Unibox />
            </Main>
          ),
          private: true,
          requiredPermissions: [PermissionName.DASHBOARD],
        },
      ]
    : []),
  // Engage — same rule as UniBox: route and sidebar entry share ENGAGE_ENABLED.
  ...(ENGAGE_ENABLED
    ? [
        {
          path: ROUTE.ENGAGE,
          element: (
            <Main>
              <Engage />
            </Main>
          ),
          private: true,
          requiredPermissions: [PermissionName.DASHBOARD],
        },
      ]
    : []),
  {
    path: ROUTE.ORDERS,
    element: (
      <Main>
        <Orders />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    path: ROUTE.FINANCE,
    element: (
      <Main>
        <Finance />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.FINANCE],
  },
  {
    path: ROUTE.FINANCE_DETAILS,
    element: (
      <Main>
        <FinanceSingle />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.FINANCE],
  },
  {
    // Advance COD moved into the Finance page as a tab (W9). The old
    // standalone route redirects to the Finance Advance COD tab; the batch
    // detail route below is kept (linked from the batches table rows).
    path: ROUTE.ADVANCE_COD,
    element: <Navigate to={`${ROUTE.FINANCE}?tab=${FinanceTab.AdvanceCod}`} replace />,
    private: true,
    requiredPermissions: [PermissionName.ADVANCE_COD],
  },
  {
    path: ROUTE.ADVANCE_COD_BATCH_DETAILS,
    element: (
      <Main>
        <AdvanceBatchSingle />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ADVANCE_COD],
  },
  {
    path: ROUTE.TICKET_CENTER,
    element: (
      <Main>
        <TicketCenter />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.TICKET_CENTER_DETAILS,
    element: (
      <Main>
        <SingleTicket />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.PICKUPS,
    element: (
      <Main>
        <Pickups />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    path: ROUTE.ADDNEWORDER,
    element: (
      <Main>
        <AddNewOrder />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.CREATE_ORDER],
    // Merchant Orders Pause — an intake surface. A paused merchant is
    // redirected away, so the URL is not a way back in.
    blockedWhenOrdersPaused: true,
  },
  {
    path: ROUTE.SELECT_CARRIER,
    // No merchant picks a carrier (owner 2026-10-28: no carrier on any
    // merchant screen). An old link, bookmark or back button lands on the
    // summary — the step Select mode always skipped to.
    element: <Navigate to={ROUTE.ORDER_SUMMARY} replace />,
    private: true,
    requiredPermissions: [PermissionName.CREATE_ORDER],
    // Merchant Orders Pause — an intake surface. A paused merchant is
    // redirected away, so the URL is not a way back in.
    blockedWhenOrdersPaused: true,
  },
  {
    path: ROUTE.SELECT_SERVICE,
    element: (
      <Main>
        {/* SVC-STEP: the service step is what every merchant has in place
            of a carrier step. */}
        <SelectService />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.CREATE_ORDER],
    // Merchant Orders Pause — an intake surface. A paused merchant is
    // redirected away, so the URL is not a way back in.
    blockedWhenOrdersPaused: true,
  },
  {
    path: ROUTE.ORDER_SUMMARY,
    element: (
      <Main>
        <OrderSummary />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.CREATE_ORDER],
    // Merchant Orders Pause — an intake surface. A paused merchant is
    // redirected away, so the URL is not a way back in.
    blockedWhenOrdersPaused: true,
  },
  {
    path: ROUTE.ORDER_EDIT,
    element: (
      <Main>
        <EditOrder />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.UPDATE_ORDER],
  },
  {
    path: `${ROUTE.ORDER_DETAILS}/:orderId`,
    element: (
      <Main>
        <OrderDetails />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    path: `${ROUTE.PICKUP_RTO_DETAILS}`,
    element: (
      <Main>
        <OrderRtoSingle />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    path: `${ROUTE.ORDER_DETAILS_RTO}/:id`,
    element: <Navigate to={`${ROUTE.PICKUP_RTO_DETAILS}/:id`} replace />,
    private: true,
  },
  {
    path: `${ROUTE.PICKUPS_DETAILS}/:id`,
    element: (
      <Main>
        <PickupSingle />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },

  {
    path: ROUTE.IMPORT,
    element: (
      <Main>
        <Import />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.BULK_IMPORT_ORDER],
    // Merchant Orders Pause — an intake surface. A paused merchant is
    // redirected away, so the URL is not a way back in.
    blockedWhenOrdersPaused: true,
  },
  {
    path: ROUTE.RESEND,
    element: (
      <Main>
        <ResendPage />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.RESOLVE_OR_REQUEST_RTO],
  },
  {
    path: ROUTE.SEARCH,
    element: (
      <Main>
        <SearchResultsPage />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.CLAIMS,
    element: (
      <Main>
        <Claims />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.VIEW_CLAIMS],
  },
  {
    path: `${ROUTE.CLAIMS_DETAILS}/:uid`,
    element: (
      <Main>
        <ClaimDetails />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.VIEW_CLAIMS],
  },
  {
    path: ROUTE.SETTINGS,
    element: <Navigate to={ROUTE.SETTINGS_PROFILE} replace />,
    private: true,
  },
  {
    path: ROUTE.SETTINGS_PROFILE,
    element: (
      <Main>
        <SettingsProfile />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_LOCATIONS,
    element: (
      <Main>
        <SettingsLocations />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_LOCATIONS_NEW,
    element: (
      <Main>
        <SettingsLocationNew />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_LOCATIONS_EDIT,
    element: (
      <Main>
        <SettingsLocationEdit />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_TEAM_MEMBERS,
    element: (
      <Main>
        <SettingsTeamMembers />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_TEAM_MEMBERS_CREATE,
    element: (
      <Main>
        <SettingsTeamMembersCreate />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_TEAM_MEMBERS_EDIT,
    element: (
      <Main>
        <SettingsTeamMembersEdit />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_ACCESS_TOKENS,
    element: (
      <Main>
        <SettingsAccessTokens />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_ACCESS_TOKENS_CREATE,
    element: (
      <Main>
        <SettingsAccessTokensCreate />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_WEBHOOKS,
    element: (
      <Main>
        <SettingsWebhooks />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_WEBHOOKS_CREATE,
    element: (
      <Main>
        <SettingsWebhooksCreate />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_WEBHOOKS_EDIT,
    element: (
      <Main>
        <SettingsWebhooksEdit />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_FILES,
    element: (
      <Main>
        <SettingsFiles />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_BRANDED_TRACKING,
    element: (
      <Main>
        <SettingsBrandedTracking />
      </Main>
    ),
    private: true,
  },
  {
    path: ROUTE.SETTINGS_CARRIERS,
    // Carrier toggles and shipping rules are not merchant-facing any more
    // (owner 2026-10-28). Old links land on the profile, as Select did.
    element: <Navigate to={ROUTE.SETTINGS_PROFILE} replace />,
    private: true,
  },
  {
    path: ROUTE.SETTINGS_SHIPPING_RULES,
    element: <Navigate to={ROUTE.SETTINGS_PROFILE} replace />,
    private: true,
  },
  {
    /*
     * 🔴 THE SAME HOLE, TWICE. InvitationsPanel was first mounted in
     * pages/settings/index.tsx — the file directly below documents that NO
     * route renders it, and the first fix for this walked straight back into
     * it. An invited merchant had a pending invitation on the server, a
     * finished component in the bundle, and no URL that reached it.
     *
     * ⭐⭐⭐ "MOUNTED IN A COMPONENT" IS NOT "REACHABLE". The only proof is a
     * route, and the test below asserts this entry exists rather than trusting
     * the import.
     *
     * NO requiredPermissions, deliberately: an invitation is addressed to a
     * PERSON, and the invitee usually holds nothing in the workspace inviting
     * them. A permission gate would bounce exactly the people it is for — which
     * is why /workspaces and /invitations carry no permission pattern on the
     * server either.
     */
    path: ROUTE.SETTINGS_WORKSPACES,
    element: (
      <Main>
        <WorkspacesSection />
      </Main>
    ),
    private: true,
  },
  {
    // Layer ② of the service-level model. The component was previously only
    // reachable through pages/settings/index.tsx, which no route renders, so
    // the merchant had no way to set an account default or a province rule.
    path: ROUTE.SETTINGS_SERVICE_PREFERENCE,
    element: (
      <Main>
        <ServicePreferenceSection />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    // UI-F4 — GET /v1/merchants/pricing-plan shipped with nothing consuming
    // it. FINANCE, not ORDER_AND_SHIPMENT: the backend gates this same route
    // group on FINANCE (config/merchant_route_permissions.php), and a
    // dashboard entry a sub-user can click into a 403 is worse than no entry.
    path: ROUTE.SETTINGS_PRICING_PLAN,
    element: (
      <Main>
        <PricingPlanSection />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.FINANCE],
  },
  {
    path: ROUTE.SETTINGS_ADDRESS_BOOK_NEW,
    element: (
      <Main>
        <SettingsAddressBookNew />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.SETTINGS_ADDRESS_BOOK_EDIT,
    element: (
      <Main>
        <SettingsAddressBookEdit />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.SETTINGS_ADDRESS_BOOK,
    element: (
      <Main>
        <SettingsAddressBook />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.DASHBOARD],
  },
  {
    path: ROUTE.SETTINGS_PRODUCT_CATALOG,
    element: (
      <Main>
        <SettingsProductCatalog />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.PRODUCT_CATALOG],
  },
  {
    path: ROUTE.SETTINGS_PRODUCT_CATALOG_NEW,
    element: (
      <Main>
        <SettingsProductCatalogNew />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.PRODUCT_CATALOG],
  },
  {
    path: ROUTE.SETTINGS_PRODUCT_CATALOG_EDIT,
    element: (
      <Main>
        <SettingsProductCatalogEdit />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.PRODUCT_CATALOG],
  },
  {
    path: ROUTE.RTO_DETAILS,
    element: (
      <Main>
        <RtoDetails />
      </Main>
    ),
    private: true,
    requiredPermissions: [PermissionName.ORDER_AND_SHIPMENT],
  },
  {
    path: ROUTE.LOGIN,
    element: <Login />,
    private: false,
  },
  {
    path: ROUTE.REGISTER,
    element: <Login />,
    private: false,
  },
  {
    path: ROUTE.VERIFY,
    element: <Verify />,
    private: false,
  },
  {
    path: ROUTE.NO_DATA,
    element: (
      <Main>
        <NoData />
      </Main>
    ),
    private: false,
  },
  {
    path: ROUTE.ERROR_400,
    element: (
      <Main>
        <ClientError />
      </Main>
    ),
    private: false,
  },
  {
    path: ROUTE.ERROR_500,
    element: (
      <Main>
        <ServerError />
      </Main>
    ),
    private: false,
  },
  // {
  //   path: ROUTE.NOT_VERIFIED,
  //   element: <NotVerified />,
  //   private: false,
  // },
  {
    path: ROUTE.MAGIC_TOKEN,
    element: <MagicToken />,
    private: false,
  },
  {
    path: ROUTE.NOT_FOUND,
    element: (
      <Main>
        <NotFound />
      </Main>
    ),
    private: false,
  },
];

export { routes, ROUTE };
